import React, { useState, useContext, useEffect } from 'react';
import { FirebaseContext } from '../App'; // Import FirebaseContext
import { getUserProfile, setUserProfile } from '../utils/firestoreService'; // Import Firestore services
import './Auth.css';

function XUsernameSetup({ onUsernameSet }) {
    // Access db, currentUser, userId, and isAuthReady from FirebaseContext
    const { db, currentUser, userId, isAuthReady } = useContext(FirebaseContext);

    const [xUsername, setXUsername] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true); // Start as loading to fetch profile
    const [existingUsername, setExistingUsername] = useState(null);

    // Effect to fetch user profile when auth is ready and user exists
    useEffect(() => {
        const fetchUserProfile = async () => {
            if (isAuthReady && currentUser && db && userId) {
                setIsLoading(true);
                const profile = await getUserProfile(db, userId);
                if (profile && profile.xUsername) {
                    setExistingUsername(profile.xUsername);
                    onUsernameSet(profile.xUsername); // Notify App.js that username is set
                }
                setIsLoading(false);
            } else if (isAuthReady && !currentUser) {
                // If auth is ready but no user (shouldn't happen with anonymous sign-in, but as a fallback)
                setIsLoading(false);
            }
        };

        fetchUserProfile();
    }, [isAuthReady, currentUser, db, userId, onUsernameSet]); // Re-run when these dependencies change

    // Handles saving the X username
    const handleSaveUsername = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setIsLoading(true);

        if (!xUsername.trim()) {
            setError("X Username cannot be empty.");
            setIsLoading(false);
            return;
        }

        if (!db || !userId) {
            setError("Firebase connection not ready. Please try again.");
            setIsLoading(false);
            return;
        }

        try {
            const success = await setUserProfile(db, userId, { xUsername: xUsername.trim() });
            if (success) {
                setMessage("X Username saved successfully!");
                setExistingUsername(xUsername.trim());
                onUsernameSet(xUsername.trim()); // Notify parent component
            } else {
                setError("Failed to save X Username. Please try again.");
            }
        } catch (err) {
            console.error("Error saving X username:", err);
            setError(`Failed to save X Username: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <p className="loading-message">Loading user profile...</p>;
    }

    // If an X username already exists for this user, display it and don't show the form
    if (existingUsername) {
        return (
            <div className="auth-container">
                <h2 className="auth-title">Welcome Back!</h2>
                <p className="auth-message">Your X Username: <strong>@{existingUsername}</strong></p>
                <p className="auth-info">You can now upload and browse game edits.</p>
            </div>
        );
    }

    // Otherwise, show the form to set the X username
    return (
        <div className="auth-container">
            <h2 className="auth-title">Verify Your X Username</h2>
            <p className="auth-info">Please enter your X (formerly Twitter) username to get started. This will identify your posts.</p>
            <form onSubmit={handleSaveUsername} className="auth-form">
                <div className="form-group">
                    <label htmlFor="xUsername">X Username:</label>
                    <input
                        type="text"
                        id="xUsername"
                        value={xUsername}
                        onChange={(e) => setXUsername(e.target.value)}
                        placeholder="e.g., @your_x_handle"
                        required
                    />
                </div>
                {error && <p className="auth-error">{error}</p>}
                {message && <p className="auth-message">{message}</p>}
                <button type="submit" className="auth-button" disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save X Username'}
                </button>
            </form>
        </div>
    );
}

export default XUsernameSetup;
